import type { Dispatch, SetStateAction } from 'react';
import type { ArrowDirection } from '../components/atomic/buttons/arrows/Arrows';
import type { NavBarTab } from '../components/composite/nav/nav_bar/NavBar';
import {
  containFitScale,
  type FitScale,
} from '../../../infrastructure/runtime/fitViewport';
import type { GiantId } from './giants/giantPositions';
import {
  MAP_HEIGHT,
  MAP_PAINT_SCALE,
  MAP_SCALE,
  MAP_WIDTH,
  mapPanStep,
} from './lands/landPositions';

export type MapScreenProps = {
  activeTab?: NavBarTab;
  /** Pass balance for HUD ticket button. */
  passesBalance?: number;
  /** Current courier license tier name for HUD button. */
  playerLevelName?: string;
  /** Average delivery rating for HUD star button. */
  averageRating?: number;
  /** Last rating delta after a completed delivery (HUD coeff). */
  lastRatingDelta?: number | null;
  onTabChange?: (tab: NavBarTab) => void;
  onTakeDelivery?: (giantId: GiantId, orderIndex: number) => void;
  onOpenTickets?: () => void;
  onOpenRating?: () => void;
  onOpenPlayerLevel?: () => void;
};

export type MapCamera = {
  x: number;
  y: number;
  /** World display scale; base is MAP_SCALE, max is MAP_ZOOM_MAX_SCALE. */
  scale: number;
};

export type MapViewSize = {
  width: number;
  height: number;
};

export type MapScreenState = {
  camera: MapCamera;
  setCamera: Dispatch<SetStateAction<MapCamera>>;
  viewSize: MapViewSize;
  fitScale: FitScale;
};

export type MapScreenModel = {
  camera: MapCamera;
  setCamera: Dispatch<SetStateAction<MapCamera>>;
  canNavigate: Record<ArrowDirection, boolean>;
  activeTab: NavBarTab;
  onTabChange?: (tab: NavBarTab) => void;
  onTakeDelivery?: (giantId: GiantId, orderIndex: number) => void;
  onOpenTickets?: () => void;
  onOpenRating?: () => void;
  onOpenPlayerLevel?: () => void;
  passesBalance?: number;
  playerLevelName?: string;
  averageRating?: number;
  lastRatingDelta?: number | null;
  viewSize: MapViewSize;
  fitScale: FitScale;
};

function clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value));
}

/** Interactive zoom: base fit scale … +44% (~20% above previous +20% cap). */
export const MAP_ZOOM_MIN_SCALE = MAP_SCALE;
export const MAP_ZOOM_MAX_SCALE = MAP_PAINT_SCALE;

export function maxCameraForScale(
  scale: number,
  viewSize: MapViewSize,
): { x: number; y: number } {
  return {
    x: Math.max(0, MAP_WIDTH * scale - viewSize.width),
    y: Math.max(0, MAP_HEIGHT * scale - viewSize.height),
  };
}

export function createInitialCamera(viewSize: MapViewSize): MapCamera {
  const max = maxCameraForScale(MAP_SCALE, viewSize);
  return {
    x: max.x / 2,
    y: max.y / 2,
    scale: MAP_SCALE,
  };
}

export const MAP_SCREEN_WORLD = {
  width: MAP_WIDTH,
  height: MAP_HEIGHT,
  scale: MAP_SCALE,
};

export { mapPanStep };

/** Clamped camera delta for one arrow step in the current CSS view. */
export function mapArrowNudge(
  camera: MapCamera,
  direction: ArrowDirection,
  viewSize: MapViewSize,
): { dx: number; dy: number } {
  const step = mapPanStep(viewSize.width);
  let dx = 0;
  let dy = 0;
  switch (direction) {
    case 'left':
      dx = -step;
      break;
    case 'right':
      dx = step;
      break;
    case 'up':
      dy = -step;
      break;
    case 'down':
      dy = step;
      break;
  }
  const next = clampCamera(
    { ...camera, x: camera.x + dx, y: camera.y + dy },
    viewSize,
  );
  return { dx: next.x - camera.x, dy: next.y - camera.y };
}

export function clampCamera(
  camera: MapCamera,
  viewSize: MapViewSize,
): MapCamera {
  const scale = clamp(
    Number.isFinite(camera.scale) ? camera.scale : MAP_SCALE,
    MAP_ZOOM_MIN_SCALE,
    MAP_ZOOM_MAX_SCALE,
  );
  const max = maxCameraForScale(scale, viewSize);
  return {
    x: clamp(camera.x, 0, max.x),
    y: clamp(camera.y, 0, max.y),
    scale,
  };
}

/**
 * Undo `.App` stretch so world CSS px map 1∶1 to screen px, then pan/zoom.
 */
export function mapWorldTransform(
  camera: MapCamera,
  fitScale: FitScale,
): string {
  const invX = fitScale.x > 0 ? 1 / fitScale.x : 1;
  const invY = fitScale.y > 0 ? 1 / fitScale.y : 1;
  return `scale(${invX}, ${invY}) translate(${-camera.x}px, ${-camera.y}px) scale(${camera.scale})`;
}

/** Top-bar HUD: same contain scale as other stretched chrome. */
export function mapHudElementScale(fitScale: FitScale) {
  return containFitScale(fitScale);
}

export function createMapScreenModel(
  props: MapScreenProps,
  state: MapScreenState,
): MapScreenModel {
  const { camera, setCamera, viewSize, fitScale } = state;
  const max = maxCameraForScale(camera.scale, viewSize);

  return {
    camera,
    setCamera,
    canNavigate: {
      left: camera.x > 0,
      right: camera.x < max.x,
      up: camera.y > 0,
      down: camera.y < max.y,
    },
    activeTab: props.activeTab ?? 'map',
    onTabChange: props.onTabChange,
    onTakeDelivery: props.onTakeDelivery,
    onOpenTickets: props.onOpenTickets,
    onOpenRating: props.onOpenRating,
    onOpenPlayerLevel: props.onOpenPlayerLevel,
    passesBalance: props.passesBalance,
    playerLevelName: props.playerLevelName,
    averageRating: props.averageRating,
    lastRatingDelta: props.lastRatingDelta,
    viewSize,
    fitScale,
  };
}
